import { create } from 'zustand';

import { patchMe } from '../api/meApi';

interface PresencePrivacy {
  online_status_visible: boolean;
  typing_indicator_enabled: boolean;
  read_receipts_enabled: boolean;
  friend_message_notifications_enabled: boolean | null;
  friend_message_notification_sound: 'none' | 'soft' | 'classic' | 'loud';
}

interface MeStoreState {
  pipDefaultOn: boolean;
  safetyCheckinEnabled: boolean;
  // B14 friends-network hydrate fields
  friendCount: number;
  pendingFriendRequestCount: number;
  presencePrivacy: PresencePrivacy;
  setPipDefaultOn: (value: boolean) => Promise<void>;
  setSafetyCheckinEnabled: (value: boolean) => Promise<void>;
  /** Initialize from a fresh GET /api/v1/auth/me response. */
  hydrateFromMe: (
    pipDefaultOn: boolean,
    safetyCheckinEnabled?: boolean,
    friendCount?: number,
    pendingFriendRequestCount?: number,
    presencePrivacy?: PresencePrivacy,
  ) => void;
}

const DEFAULT_PRESENCE_PRIVACY: PresencePrivacy = {
  online_status_visible: true,
  typing_indicator_enabled: true,
  read_receipts_enabled: true,
  friend_message_notifications_enabled: null,
  friend_message_notification_sound: 'soft',
};

/**
 * Holds user-settings client mirror (B09a: `pip_default_on`, B10:
 * `safety_checkin_enabled`). Populated via /api/v1/auth/me; persisted
 * via PATCH /api/v1/auth/me with allow-listed body.
 */
export const useMeStore = create<MeStoreState>((set) => ({
  pipDefaultOn: false,
  safetyCheckinEnabled: true, // B10 default opt-in
  friendCount: 0,
  pendingFriendRequestCount: 0,
  presencePrivacy: DEFAULT_PRESENCE_PRIVACY,

  hydrateFromMe: (pipDefaultOn, safetyCheckinEnabled, friendCount, pendingFriendRequestCount, presencePrivacy) => set({
    pipDefaultOn,
    safetyCheckinEnabled: safetyCheckinEnabled ?? true,
    friendCount: friendCount ?? 0,
    pendingFriendRequestCount: pendingFriendRequestCount ?? 0,
    presencePrivacy: presencePrivacy ?? DEFAULT_PRESENCE_PRIVACY,
  }),

  setPipDefaultOn: async (value) => {
    const response = await patchMe({ pip_default_on: value });
    set({ pipDefaultOn: response.pip_default_on });
  },

  setSafetyCheckinEnabled: async (value) => {
    const response = await patchMe({ safety_checkin_enabled: value });
    if (response.safety_checkin_enabled !== undefined) {
      set({ safetyCheckinEnabled: response.safety_checkin_enabled });
    } else {
      set({ safetyCheckinEnabled: value });
    }
  },
}));
